// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Utils
import getClientConfig from '../../../../../../utils/getClientConfig';
import { canSave } from '../../../../../../../common/permissions/permissionUtil';
import {
    isPlatformElectronMac,
    isPlatformModernMobileApp,
} from '../../../../../../platform/utils/platformDetailsUtils';
import { hasSecretLinkAcl } from '../../../../../../../common/permissions/elementPermissionsUtil';
import {
    getElementId,
    getIsPublishedPasswordProtected,
} from '../../../../../../../common/elements/utils/elementPropertyUtils';

// Selectors
import { publishBoardPopupSelector } from './publishBoardSelector';

// Actions
import { deselectAllElements } from '../../../../../../element/selection/selectionActions';

// Components
import PublishLinkToggle from './settings/PublishLinkToggle';
import WelcomeMessageToggle from './settings/WelcomeMessageToggle';
import PublishBoardEmbedToggle from './settings/PublishBoardEmbedToggle';
import PublishBoardCommentToggle from './settings/PublishBoardCommentToggle';
import PublishBoardDownloadFilesToggle from './settings/PublishBoardDownloadFilesToggle';
import PublishBoardViewersSection from './viewers/PublishBoardViewersSection';
import PublishBoardPasswordToggle from './settings/PublishBoardPasswordToggle';

// Constants
import { PUBLISH_PREVIEW_PATHNAME } from '../../../../../preview/previewConstants';

// Styles
import './PublishBoardPanel.scss';

const mapDispatchToProps = (dispatch) => ({
    dispatchDeselectAll: () => dispatch(deselectAllElements()),
});

const PublishBoardPanel = (props) => {
    const {
        dispatchDeselectAll,
        currentBoard,
        currentBoardId,
        userPermission,
        publishedAncestorBoard,
        platformDetails,
        setErrorToast,
    } = props;

    // Published board viewers logic
    const isPublished = !!publishedAncestorBoard;
    const isPublishedAncestorPasswordProtected =
        getIsPublishedPasswordProtected(publishedAncestorBoard) && publishedAncestorBoard !== currentBoard;

    const [publishLinkEnabled, setPublishLinkEnabled] = React.useState(isPublished);
    const [passwordEnabled, setPasswordEnabled] = React.useState(getIsPublishedPasswordProtected(currentBoard));

    React.useEffect(() => {
        dispatchDeselectAll();
    }, []);

    const hasLegacyPublicPermissions = hasSecretLinkAcl(publishedAncestorBoard);

    const canEditLink = canSave(userPermission);
    const publishedAncestorBoardId = getElementId(publishedAncestorBoard);

    const showSharedAncestorBoardLink =
        publishedAncestorBoardId !== currentBoardId && publishedAncestorBoard && canEditLink;

    const canModifySettings = canEditLink && publishLinkEnabled && !showSharedAncestorBoardLink;

    const previewLinkUrl = isPlatformElectronMac(platformDetails)
        ? `${getClientConfig().apiRoot}/${PUBLISH_PREVIEW_PATHNAME}/${currentBoardId}`
        : `/${PUBLISH_PREVIEW_PATHNAME}/${currentBoardId}`;

    const showPreviewLink = !isPlatformModernMobileApp(platformDetails);

    return (
        <div className="PublishBoardPanel ShareModalColumn">
            <h1>Share a read-only link</h1>
            <p className="explanation">
                Perfect for sharing with clients. No signup required to&nbsp;view.{' '}
                {showPreviewLink && (
                    <a href={previewLinkUrl} target="_blank" rel="noopener noreferrer">
                        Preview the link
                    </a>
                )}
            </p>
            <PublishLinkToggle
                {...props}
                publishLinkEnabled={publishLinkEnabled}
                setPublishLinkEnabled={setPublishLinkEnabled}
                canEditLink={canEditLink}
                showSharedAncestorBoardLink={showSharedAncestorBoardLink}
                hasLegacyPublicPermissions={hasLegacyPublicPermissions}
                publishedAncestorBoardId={publishedAncestorBoardId}
                setErrorToast={setErrorToast}
            />
            <PublishBoardCommentToggle
                {...props}
                showSharedAncestorBoardLink={showSharedAncestorBoardLink}
                isPublished={publishLinkEnabled}
                canModifySettings={canModifySettings}
            />
            <PublishBoardDownloadFilesToggle
                {...props}
                showSharedAncestorBoardLink={showSharedAncestorBoardLink}
                isPublished={publishLinkEnabled}
                canModifySettings={canModifySettings}
            />
            <WelcomeMessageToggle
                {...props}
                showSharedAncestorBoardLink={showSharedAncestorBoardLink}
                isPublished={publishLinkEnabled}
                canModifySettings={canModifySettings}
            />
            <PublishBoardPasswordToggle
                {...props}
                showSharedAncestorBoardLink={showSharedAncestorBoardLink}
                isPublished={publishLinkEnabled}
                canModifySettings={canModifySettings}
                passwordEnabled={passwordEnabled}
                isPublishedAncestorPasswordProtected={isPublishedAncestorPasswordProtected}
                setPasswordEnabled={setPasswordEnabled}
            />
            <PublishBoardEmbedToggle
                {...props}
                showSharedAncestorBoardLink={showSharedAncestorBoardLink}
                // Don't allow the embed link to be enabled if this or an ancestor is password protected
                passwordEnabled={isPublishedAncestorPasswordProtected || passwordEnabled}
                isPublished={publishLinkEnabled}
                canModifySettings={canEditLink && publishLinkEnabled}
                hasLegacyPublicPermissions={hasLegacyPublicPermissions}
            />
            <PublishBoardViewersSection
                currentBoard={currentBoard}
                showSharedAncestorBoardLink={showSharedAncestorBoardLink}
                isPublished={publishLinkEnabled}
                platformDetails={platformDetails}
            />
        </div>
    );
};

PublishBoardPanel.propTypes = {
    gridSize: PropTypes.number,
    currentBoardId: PropTypes.string,
    currentBoard: PropTypes.object,
    dispatchDeselectAll: PropTypes.func,
    platformDetails: PropTypes.object,

    publishUpdating: PropTypes.bool,
    setPublishUpdating: PropTypes.func,

    userPermission: PropTypes.number,
    publishedAncestorBoard: PropTypes.object,
    currentBoardPublishedPermissionId: PropTypes.string,
    setErrorToast: PropTypes.func,
};

export default connect(publishBoardPopupSelector, mapDispatchToProps)(PublishBoardPanel);
